import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, Hash } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { LanguageSelector } from '../components/LanguageSelector';

interface JoinRoomPageProps {
  onBack: () => void;
  onJoinRoom: (roomCode: string, playerName: string) => void;
}

export const JoinRoomPage: React.FC<JoinRoomPageProps> = ({
  onBack,
  onJoinRoom,
}) => {
  const { t } = useTranslation();
  const [roomCode, setRoomCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleJoinRoom = async () => {
    if (!roomCode.trim() || !playerName.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      await onJoinRoom(roomCode.trim().toUpperCase(), playerName.trim());
    } catch (err) {
      setError(t('joinRoom.roomNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const canJoin = roomCode.trim().length >= 4 && playerName.trim().length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            {t('joinRoom.title')}
          </h1>
          <p className="text-white/70">
            Ingresa el código de la sala y tu nombre para unirte
          </p>
        </div>

        <Card>
          <div className="space-y-6">
            <Input
              label={t('joinRoom.roomCode')}
              placeholder={t('joinRoom.roomCodePlaceholder')}
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              icon={<Hash className="w-5 h-5" />}
              maxLength={6}
            />

            <Input
              label={t('joinRoom.playerName')}
              placeholder={t('joinRoom.playerNamePlaceholder')}
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              icon={<Users className="w-5 h-5" />}
            />

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-400 rounded-xl">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleJoinRoom}
              disabled={!canJoin || loading}
              loading={loading}
              icon={<Users className="w-5 h-5" />}
            >
              {t('joinRoom.join')}
            </Button>
          </div>
        </Card>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-white/60 text-sm">
            ¿No tienes un código? Pide a un amigo que cree una sala
          </p>
        </div>
      </div>
    </div>
  );
};
